<template>
  <Teleport v-if="isModalOpen" to="body">
    <cl-modal
      :close-modal-text="$t('close')"
      :visible="true"
      test-id="block-modal"
      @on-close="onClose"
    >
      <template #[`header.title`]>
        <cl-heading :size="'h4'" class="tw-mb-[0]">{{
          $t("add-exemption")
        }}</cl-heading>
      </template>
      <cl-callout
        :close-text="$t('close')"
        v-if="isMspRoute"
        test-id="exemptions-add-info"
        variant="info"
      >
        <template #title>
          {{ $t("all-customers-title") }}
        </template>
        <template #message>
          {{ $t("exemptions-all-customers") }}
        </template>
      </cl-callout>
      <form @submit.prevent>
        <div class="tw-flex tw-gap-4">
          <cl-form-group class="tw-w-full">
            <cl-form-label
              label-for="type"
              class="hover:tw-cursor-pointer tw-mr-4"
              >{{ t("label-type") }}</cl-form-label
            >
            <input
              type="hidden"
              name="type"
              data-test-id="exemptions-add-type"
              :value="form.type"
            />
            <cl-select
              id="type"
              v-model="form.type"
              :placeholder="t('select-address-type')"
              :is-disabled="Boolean(defaultType)"
              data-test-id="exemptions-add-type-select"
            >
              <cl-select-item
                v-for="option in typeOptions"
                :value="option.value"
                :textValue="option.text"
              >
                {{ option.text }}
              </cl-select-item>
            </cl-select>
          </cl-form-group>
          <cl-form-group class="tw-w-full">
            <cl-form-label label-for="email">
              {{ $t("address") }}
            </cl-form-label>
            <cl-form-input
              v-model:value="form.value"
              id="email"
              name="email"
              data-test-id="exemptions-add-email"
              :state="
                isBlockedDomain
                  ? false
                  : !v$.value.$dirty
                    ? null
                    : !v$.value.$error
              "
              :placeholder="t('enter-address')"
              :disabled="Boolean(!form.type || defaultValue)"
            />
            <span
              v-if="isBlockedDomain || v$.value.$error"
              class="tw-text-system-error-500 tw-text-xs"
              >{{
                isBlockedDomain ? $t("allow-domain") : validationErrorMessage
              }}</span
            >
          </cl-form-group>
        </div>
        <exemptions-bypass-checks v-model:bypassChecks="form.bypassChecks" />
        <cl-form-group>
          <cl-form-label label-for="comment">
            {{ t("comment-optional") }}
          </cl-form-label>
          <cl-form-textarea
            id="comment"
            name="comment"
            v-model:value="v$.comment.$model as string"
            :state="!v$.comment.$dirty ? undefined : !v$.comment.$error"
            :placeholder="t('comment-optional')"
            :data-test-id="'block-list-comment'"
            class="tw-h-24 tw-resize-none tw-mb-1"
          />
          <div
            class="tw-flex tw-flex-row-reverse tw-items-center tw-justify-between"
          >
            <span
              class="tw-text-xs"
              :class="{ 'tw-text-system-error-500': v$.comment.$invalid }"
            >
              {{ v$.comment.$model.length }} / 250
              {{ t("characters") }}
            </span>
            <span
              v-if="v$.comment.$invalid"
              class="tw-text-xs tw-text-system-error-500"
            >
              {{ v$.comment.$errors[0].$message }}
            </span>
          </div>
        </cl-form-group>
      </form>
      <template #[`footer.decline`]>
        <cl-button
          variant="secondary-ghost"
          data-test-id="block-modal-cancel"
          @on-click="onClose"
        >
          {{ $t("cancel") }}
        </cl-button>
      </template>
      <template #[`footer.accept`]>
        <cl-button
          variant="secondary"
          data-test-id="exemptions-add-confirm"
          :disabled="isConfirmDisabled"
          @on-click="onConfirm"
        >
          {{ $t("add-exemption") }}
        </cl-button>
      </template>
    </cl-modal>
  </Teleport>
</template>
<script setup lang="ts">
import { useToggle } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, maxLength, email, helpers } from "@vuelidate/validators";

import { trackEvent } from "@/utils/track-event";
import { ExemptionsType } from "./exemptions.types";
import { BypassCheck } from "./exemptions.types";
import { getEnabledChecks } from "./Exemptions.utils";
import { useExemptions } from "./useExemptions.store";
import { useToastStore } from "@/components/TToasts/toast.store.ts";
import { isAxiosError } from "axios";
import { useGlobalOrganisationIDBasedOnRoute } from "@/composables/useGlobalOrganisationIDBasedOnRoute";
import { useIsMspRoute } from "@/composables/useIsMspRoute";
import { DOMAIN_REGEX } from "@/utils/regex";
import { ipAddressOrCIDR } from "../validators";
import ExemptionsBypassChecks from "./exemptions-bypass-checks/exemptions-bypass-checks.vue";

const props = defineProps<{
  defaultType?: ExemptionsType;
  defaultValue?: string;
  autoOpen?: boolean;
}>();

const emit = defineEmits<{
  (event: "success"): void;
  (event: "close"): void;
}>();

const { t } = useI18n();
const { globalOrganisationId: organisationID } =
  useGlobalOrganisationIDBasedOnRoute();
const { isMspRoute } = useIsMspRoute();
const {
  addExemption,
  freeProviderDomains,
  isFetchingFreeProviderDomains,
  getFreeProviderDomains,
} = useExemptions();
const toastStore = useToastStore();
const [isModalOpen, toggleModalOpen] = useToggle(false);

onMounted(() => {
  if (props.autoOpen) toggleModalOpen(true);
});
const isFetching = ref(false);

const defaultForm: {
  type: ExemptionsType | "";
  value: string;
  comment: string;
  bypassChecks: Record<BypassCheck, boolean>;
} = {
  type: "",
  value: "",
  comment: "",
  bypassChecks: {
    antispoof: false,
    geoip: false,
    spam: false,
    frequently_exploited_domains: false,
    graymail: false,
    suspicious_intent: false,
    known_malicious_sender: false,
    phishing: false,
    malware: false,
    malicious_intent: false,
    fraud: false,
  },
};

const form = reactive<{
  type: ExemptionsType | "";
  value: string;
  comment: string;
  bypassChecks: Record<BypassCheck, boolean>;
}>({
  ...defaultForm,
  value: props.defaultValue ?? "",
  type: props.defaultType ?? "",
  bypassChecks: { ...defaultForm.bypassChecks },
});

const rules = computed(() => ({
  value: (() => {
    switch (form.type) {
      case "domain":
        return {
          required,
          matchRegex: (value: string) => DOMAIN_REGEX.test(value),
          supported: (value: string) =>
            !freeProviderDomains.value.includes(value),
        };
      case "ip":
        return { required, ipAddressOrCIDR };
      default:
        return { required, email };
    }
  })(),
  comment: { maxLength: maxLength(250) },
  bypassChecks: {
    atLeastOne: helpers.withMessage(
      "At least one bypass check must be selected",
      (value: Record<BypassCheck, boolean>) =>
        Object.values(value).some((v) => v),
    ),
  },
}));
const v$ = useVuelidate(rules, form);

const typeOptions = computed<{ value: ExemptionsType; text: string }[]>(() => [
  {
    value: "email",
    text: t("email-address"),
  },
  {
    value: "domain",
    text: t("domain"),
  },
  {
    value: "ip",
    text: t("ip-address"),
  },
]);

const isFormValid = computed(
  () => form.value && Object.values(form.bypassChecks).some((v) => v),
);

const isBlockedDomain = computed(
  () => form.type === "domain" && v$.value.value.supported?.$invalid === true,
);

const isConfirmDisabled = computed(
  () =>
    isFetching.value ||
    isFetchingFreeProviderDomains.value ||
    !isFormValid.value ||
    isBlockedDomain.value,
);

const validationErrorMessage = computed(() => {
  switch (form.type) {
    case "domain":
      return t("allow-domain-format");
    case "ip":
      return t("ip-address-invalid");
    default:
      return t("email-invalid");
  }
});

const enabledChecks = computed(() => getEnabledChecks(form.bypassChecks));

const resetForm = () => {
  Object.assign(form, defaultForm, {
    type: props.defaultType,
    bypassChecks: { ...defaultForm.bypassChecks },
  });
  v$.value.$reset();
};

const open = () => {
  trackEvent({
    category: `Exemptions`,
    action: "add start",
    name: form.type,
  });
  toggleModalOpen(true);
};

defineExpose({ open });

const onClose = () => {
  resetForm();
  toggleModalOpen(false);
  emit("close");
};

const onConfirm = async () => {
  trackEvent({
    category: "Exemptions",
    action: "add confirm",
    name: form.type,
  });

  const isValid = await v$.value.$validate();

  if (!isValid || !form.value) {
    return;
  }
  if (!organisationID.value) {
    return;
  }
  if (!form.type) {
    return;
  }

  try {
    isFetching.value = true;

    await addExemption({
      organisationID: organisationID.value,
      type: form.type,
      payload: {
        value: form.value,
        skipped_checks: enabledChecks.value,
        comment: form.comment,
      },
    });

    toastStore.displayToast({
      title: t("success"),
      message:
        enabledChecks.value.length === 1
          ? t("exemption-added-one-check")
          : t("exemptions-added", {
              number: enabledChecks.value.length,
            }),
      duration: 3000,
      variant: "success",
      testId: "exemptions-add-toast-success",
    });

    trackEvent({
      category: "Exemptions",
      action: "add success",
      name: form.type,
    });

    onClose();
    emit("success");
  } catch (err: unknown) {
    if (isAxiosError(err)) {
      if (err.response?.status === 409) {
        toastStore.displayToast({
          title: t("error"),
          message: t("exemption-exists"),
          duration: 3000,
          variant: "danger",
          testId: "exemptions-add-toast-error",
        });
      } else {
        toastStore.displayToast({
          title: t("error"),
          message: t("generic-error"),
          duration: 3000,
          variant: "danger",
          testId: "exemptions-add-toast-error",
        });
      }
    }
  } finally {
    isFetching.value = false;
  }
};

watch(
  () => form.type,
  (type) => {
    if (type === "domain" && freeProviderDomains.value.length === 0) {
      getFreeProviderDomains();
    }
  },
  { immediate: true },
);

watch(
  () => props.defaultType,
  (newType) => {
    form.type = newType ?? "";
  },
);
</script>
